import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { install } from "resize-observer";
// pages
import EstimationPage from "./EstimationPage";
import PaymentConfirmationPage from "./PaymentConfirmationPage";
import PaymentPage from "./PaymentPage";
// components
import PoweredBy from "../components/PoweredBy";
import JourneysPage from "./JourneysPage";
import ErrorPage from "./ErrorPage";
// hooks
import useEmbedded from "../hooks/useEmbedded";
// utils
import { getConfigByMerchantCode } from "../utils/merchantConfig";
// @interfaces
import { IPurchase } from "../@interfaces/purchase.interface";
import {
  ICarbonOffsetAmount, ICreateMultiplePurchaseByQuoteData,
  IQuote, IQuoteByFlightData,
  IQuoteBySurfaceTravelPrivateData,
  IQuoteBySurfaceTravelPublicData,
  IQuoteFixed
} from "../@interfaces/quote.interface";
import { IBasket } from "../@interfaces/basket.interface";
import { IMerchantConfig } from "../@interfaces/config.interface";
import { JourneyDataType, QuoteTravelData, TravelRequestType } from "../@types/travel";
// api
import { createMultiplePurchaseByQuote } from "../api/quoteApi";
import {
  createEstimateByFlight,
  createEstimateBySurfaceTravelPrivate,
  createEstimateBySurfaceTravelPublic,
  createEstimateFixed,
} from "../api/estimateApi";
import { getBasketById } from "../api/projectApi";
// utils
import { getFullAirportName } from "../utils/calculations";
import { getTotalCo2Emissions, getTotalDistance } from "../utils/estimate";
import { sendAnalyticsEvent, setWidgetAnalyticEventParams } from "../utils/analytics"
// styles
import styles from "../styles/pages/JourneyWidget.module.css";
import { collectActivityMetadata } from "../utils/activityMetadata";

if (!window.ResizeObserver) install();

const Journey: FC = () => {
  const { postMessageToParent } = useEmbedded();

  const widgetWrapperRef = useRef<HTMLDivElement>(null);
  const [dynamicHeight, setDynamicHeight] = useState(0);

  const [activeStep, setActiveStep] = useState(0);
  const [merchantConfig, setMerchantConfig] = useState<IMerchantConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState<IQuote | null>(null);
  const [basket, setBasket] = useState<IBasket | null>(null);
  const [userEmail, setUserEmail] = useState("");
  const [purchase, setPurchase] = useState<IPurchase | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [journeysData, setJourneysData] = useState<JourneyDataType[] | []>([]);
  const [fetchError, setFetchError] = useState<{ code: null | string, message: string }>({ code: null, message: '' });

  const [selectedParams, setSelectedParams] = useState<{ currency: string, basket: IBasket | null, offsetRatio: number}>({
    currency: 'USD',
    basket: null,
    offsetRatio: merchantConfig?.defaultRatioSelection || 1,
  });

  const search = new URLSearchParams(useLocation().search);
  const merchantCode = search.get("merchantCode") || "CARBONCLICK";
  const programmeCode = search.get("programmeCode");
  const currencyFromUrl = search.get("currency");

  const carbonOffsetAmount = useMemo<ICarbonOffsetAmount>(() => {
    return quote?.carbonOffsetQuantity || {value: 0, unit: 'KG'};
  }, [quote]);

  useEffect(() => {
    if (currencyFromUrl) {
      setSelectedParams(prev => ({...prev, currency: currencyFromUrl}));
    } else if (merchantConfig) {
      setSelectedParams(prev => ({...prev, currency: merchantConfig?.locales.currencies[0]}));
    } else {
      setSelectedParams(prev => ({...prev, currency: "USD"}));
    }
  }, [currencyFromUrl, merchantConfig]);

  useEffect(() => {
   // this useffect might fire two times during development due to React.StrictMode
    setLoading(true);
    getConfigByMerchantCode(merchantCode)
      .then((merchantConfig) => {
        setWidgetAnalyticEventParams({
          merchant_code: merchantCode,
          programme_code: programmeCode,
          widget_type: "Journey"
        });
        sendAnalyticsEvent('widget_loaded')
        setMerchantConfig(merchantConfig);
      })
      .catch((error) => {
        setFetchError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantCode]);

  const restoreDefaultState = () => {
    setActiveStep(0);
    setPurchase(null);
    setQuote(null);
    setJourneysData([]);
  };

  const onCreateEstimateClick = async (quoteData: QuoteTravelData, type: TravelRequestType, mode?: string) => {
    createEstimate(quoteData, type, mode);
  }

  const onCreateFixedEstimateClick = async (fixedEstimateData: any) => {
    createEstimate(fixedEstimateData, "FIXED");
  }

  const deleteQuote = (id: string) => {
    const res = journeysData?.filter((el: any) => el?.estimate?.estimateId !== id);
    setJourneysData(res);
  }

  const createEstimate = async (quoteData: QuoteTravelData, type: TravelRequestType, calculateMode?: string) => {  
    setIsLoading(true);
    try {
      let estimateResponse = undefined;
      switch (type) {
        case "PUBLIC":
          estimateResponse = await createEstimateBySurfaceTravelPublic(quoteData as IQuoteBySurfaceTravelPublicData);
          break;
        case "PRIVATE":
          estimateResponse = await createEstimateBySurfaceTravelPrivate(quoteData as IQuoteBySurfaceTravelPrivateData);
          break;
        case "FLIGHT":
          estimateResponse = await createEstimateByFlight(quoteData as IQuoteByFlightData);
          break;
        case "FIXED":
          estimateResponse = await createEstimateFixed(quoteData as IQuoteFixed);
          break;
      }
      const estimate = estimateResponse?.data;

      if (estimate) {
        const quoteDataCopy = JSON.parse(JSON.stringify(quoteData));

        if (quoteDataCopy?.flights) {
          const newAir = quoteDataCopy?.flights?.map((el: { origin: string, destination: string, cabinClass: string }) => ({
            ...el,
            origin: getFullAirportName(el?.origin),
            destination: getFullAirportName(el?.destination),
          }));

          quoteDataCopy.flights = newAir;
        }
        if (estimate.distanceCovered && estimate.distanceUnit) {
          quoteDataCopy.distance = { value: estimate.distanceCovered, unit:estimate.distanceUnit };
        }


        const fullQuoteData = { ...quoteDataCopy, estimate, type, mode: calculateMode, transportMethod: quoteDataCopy.transportMethod || type };

        setJourneysData((current : any) => {
          const newJourneyData = [...current , fullQuoteData];
          const totalCo2Kgs = (newJourneyData as JourneyDataType[])?.reduce((a, b) => a + b?.estimate?.co2eQuantity, 0).toString();
          sendAnalyticsEvent('view_item', { 
            co2Kgs: totalCo2Kgs,
            itemName: "Journey Offset",
            price: { currency: "NZD", value: "0.01" }
          })
          return newJourneyData;
        });
        setIsLoading(false);
        setActiveStep(1);
      }
    } catch (error: any) {
      setFetchError({ code: null, message: error || 'Something went wrong' });
    }
  }

  const [estimateIds, setEstimateIds] = useState<string[]>([]);


  const createMultipleQuoteByEstimate = useCallback(async (params: any) => {
    setIsLoading(true);

    const dataToSend :ICreateMultiplePurchaseByQuoteData = {
      merchantCode: merchantCode,
      currency: 'USD',
      estimateIds: estimateIds,
      activityType: "JOURNEY",
      providedAmountFeeMode: "Gross",
      paymentProvider: "STRIPE",
      maximumFeesRatio: 0.4,
      selectedOffsetRatio: 1,
      activityMetadata: collectActivityMetadata(journeysData) || undefined
    }

    if (params?.basket) {
      dataToSend.basketCode = params.basket.code;
    } else if (selectedParams.basket) {
      dataToSend.basketCode = selectedParams.basket.code;
    }
    
    if (params?.currency) {
      dataToSend.currency = params.currency
    } else if (selectedParams.currency) {
      dataToSend.currency = selectedParams.currency;
    }

    if (params?.offsetRatio) {
      dataToSend.selectedOffsetRatio = params.offsetRatio;
    } else if (selectedParams.offsetRatio) {
      dataToSend.selectedOffsetRatio = selectedParams.offsetRatio;
    }
  
    try {
      const quoteResponse = await createMultiplePurchaseByQuote(dataToSend);
      const quote = quoteResponse?.data;
      if (quote?.quoteId) {
        setQuote(quote);
        sendAnalyticsEvent('begin_checkout', { co2Kgs: quote.carbonOffsetQuantity.value.toString() as string, itemName: "Journey Offset", price: quote.offsetPrice })
        const basketResult = await getBasketById(quote?.basket?.id);

        if (basketResult?.data) {
          setBasket(basketResult.data);
        }
        setIsLoading(false);
        setActiveStep(2);
      }
    } catch (error: any) {
      setFetchError({ code: null, message: error || 'Something went wrong' });
    }
  }, [estimateIds, journeysData, merchantCode, selectedParams])

  useEffect(() => {
    if (!widgetWrapperRef?.current) {
      return;
    }

    const observerCallback = (entries: ResizeObserverEntry[]) => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }

        const height = widgetWrapperRef?.current?.offsetHeight;

        if (height && height !== dynamicHeight) {
          setDynamicHeight(height);
        }
      });
    };

    const resizeObserver = new ResizeObserver(observerCallback);

    resizeObserver.observe(widgetWrapperRef.current);

    if (dynamicHeight) {
      postMessageToParent({
        type: 'POST_CARBONCLICK_WIDGET_DIMENSIONS',
        payload: {
          widgetCode: 'JOURNEY_CALCULATOR',
          height: dynamicHeight
        },
      });
    }

    return function cleanup() {
      resizeObserver.disconnect();
    }
  }, [dynamicHeight, postMessageToParent]);

  const totalDistance = getTotalDistance(journeysData);
  const totalCo2Emissions = getTotalCo2Emissions(journeysData);

  const JourneysPageSubtitle = totalDistance && totalCo2Emissions && (
    <div className={'journeys-summary'}>
      <p>Your Journey so far: <span className="color-primary">{totalDistance}</span></p>
      <p>Carbon footprint: <span className="color-primary">{totalCo2Emissions}</span></p>
    </div>
  )

  const renderBody = (step: number, merchantConfig: IMerchantConfig) => {
    if (step === 0) {
      return (
        <EstimationPage
          merchantCode={merchantCode}
          merchantConfig={merchantConfig}
          selectedCurrency={selectedParams.currency}
          isLoading={isLoading}
          onOffsetClick={onCreateEstimateClick}
          onFixedOffsetClick={onCreateFixedEstimateClick}
          JourneysPageSubtitle={JourneysPageSubtitle}
        />
      );
    }

    if (activeStep === 1) {
      return (
        <JourneysPage
          primaryData={journeysData}
          setActiveStep={setActiveStep}
          deleteQuote={deleteQuote}
          createMultipleQuoteByEstimate={createMultipleQuoteByEstimate}
          setEstimateIds={setEstimateIds}
          isLoading={isLoading}
          JourneysPageSubtitle={JourneysPageSubtitle}
          restoreDefaultState={restoreDefaultState}
        />
      );
    }

    if (activeStep === 2) {
      return (
        <PaymentPage
          programmeCode={programmeCode}
          basket={basket}
          quote={quote}
          setUserEmail={setUserEmail}
          restoreDefaultState={restoreDefaultState}
          setPurchase={setPurchase}
          setActiveStep={setActiveStep}
          offsetAmount={carbonOffsetAmount}
          disableCurrencyList={false} // !!currencyFromUrl
          selectedParams={selectedParams}
          setSelectedParams={setSelectedParams}
          merchantConfig={merchantConfig}
          createMultipleQuoteByEstimate={createMultipleQuoteByEstimate}
        />
      );
    }

    if (activeStep === 3) {
      return (
        <PaymentConfirmationPage
          purchase={purchase}
          basket={basket}
          userEmail={userEmail}
          restoreDefaultState={restoreDefaultState}
          merchantCode={merchantCode}
          merchantConfig={merchantConfig}
        />
      );
    }
  }

  if (loading) {
    return <div className={styles.loader}></div>;
  }

  return (
    <div ref={widgetWrapperRef}>
      {(merchantConfig && !fetchError?.message) && (
        <div
          className={`${styles.widgetWrapper} journeyWidget_wrapper translate-dynamic delayed-fadein`}
          style={{ minHeight: merchantConfig ? "786px" : "100%" }}
        >
          {renderBody(activeStep, merchantConfig)}
          {merchantConfig?.displayPoweredBy &&
            <PoweredBy />
          }
        </div>
      )}

      {fetchError?.message && (
         <ErrorPage
          error={fetchError}
          merchantCode={merchantCode}
        />
      )}
    </div>
  );
};

export default Journey;
